// Content sourced from the existing orthospine.care website.
// Items marked as placeholder must be confirmed/updated by the clinic.

export const clinicInfo = {
  name: "Ortho Spine Care",
  shortName: "Ortho Spine Care",
  // Official Orthospine logo. To change the logo later, replace this single value —
  // it updates the header, footer and every branded spot automatically.
  logo: "https://storage.helloreaddy.io/project_files/afe7bf6d-1a42-42e3-a1b8-8dc30048a3f0/d7fff1d7-35ab-406e-a3ab-12de668ad567_compressed_logooo.webp",
  tagline: "Orthopaedic & Spine Surgery, Trichy",
  phoneDisplay: "+91 99433 16050",
  phoneHref: "tel:+919943316050",
  whatsappHref: "https://wa.me/919943316050",
  // Add the confirmed clinic email address here (e.g. "info@orthospine.care").
  // While this is empty, the email row is hidden across the site instead of showing a placeholder.
  email: "",
  addressLine1: "Srirangam, Thiruvanaikoil",
  addressLine2: "Tiruchirappalli, Tamil Nadu 620 005",
  addressFull: "Srirangam, Thiruvanaikoil, Tiruchirappalli, Tamil Nadu 620 005",
  city: "Trichy",
  region: "Tamil Nadu",
  country: "India",
  mapEmbed:
    "https://maps.google.com/maps?q=Srirangam%2C%20Thiruvanaikoil%2C%20Tiruchirappalli%2C%20Tamil%20Nadu%20620005&t=m&z=12&output=embed",
  mapDirections:
    "https://www.google.com/maps/dir/?api=1&destination=Srirangam%2C+Thiruvanaikoil%2C+Tiruchirappalli%2C+Tamil+Nadu+620005",
  // Add the confirmed opening hours here, one entry per line (e.g. "Mon – Sat: 9:00 AM – 6:00 PM").
  // While this list is empty, the opening-hours row is hidden across the site instead of showing a placeholder.
  hours: [] as string[],
};

// Main navigation. Spine Care and Orthopaedic Care now live inside the Services
// section (see serviceMenu below) instead of being separate top-level items.
export const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

// The Services dropdown / sub-navigation. Spine Care and Orthopaedic Care are the
// two primary specialist service areas, reached through the Services section.
export const serviceMenu = [
  {
    label: "All Services",
    href: "/services",
    icon: "ri-layout-grid-line",
    text: "The full overview of everything we treat",
  },
  {
    label: "Spine Care",
    href: "/services/spine-care",
    icon: "ri-body-scan-line",
    text: "Neck & back pain, disc problems & spine surgery",
  },
  {
    label: "Orthopaedic Care",
    href: "/services/orthopaedic-care",
    icon: "ri-focus-3-line",
    text: "Joints, fractures, sports injuries & arthritis",
  },
];

export const heroSlides = [
  {
    image:
      "https://readdy.ai/api/search-image?query=Photorealistic%20modern%20orthopaedic%20and%20spine%20clinic%20interior%20with%20soft%20royal%20blue%20cyan%20and%20violet%20ambient%20lighting%2C%20clean%20minimal%20premium%20healthcare%20architecture%20with%20wide%20negative%20space%20and%20no%20people%2C%20editorial%20photography&width=1600&height=900&seq=ospc-hero-2026-a&orientation=landscape",
    alt: "Complete orthopaedic and spine care at Ortho Spine Care, Trichy",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Photorealistic%20bright%20premium%20orthopaedic%20consultation%20room%20with%20anatomical%20spine%20and%20joint%20models%20on%20a%20shelf%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20healthcare%20interior%20with%20wide%20negative%20space%20and%20no%20people%2C%20editorial%20photography&width=1600&height=900&seq=ospc-hero-2026-b&orientation=landscape",
    alt: "Advanced spine and joint treatment at Ortho Spine Care",
  },
];

export const trustHighlights = [
  {
    icon: "ri-user-heart-line",
    title: "Advanced Expertise",
    text: "Dr. N. Ajit Kumar, Senior Consultant in Orthopaedic and Spine Surgery, is known for a patient-centric approach to trauma, joint disorders and spine-related diseases.",
  },
  {
    icon: "ri-microscope-line",
    title: "Minimally Invasive Approaches",
    text: "Our team offers minimally invasive spine procedures aimed at lasting relief from long-standing neck and back pain, plus the latest arthroscopy and arthroplasty techniques.",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Compassionate Care",
    text: "A dedicated, experienced team supports post-operative care and rehabilitation, with a well-equipped ICU and intensive care support for high-risk patients.",
  },
];

export const stats = [
  { value: "10+", label: "Years of surgical experience" },
  { value: "2000+", label: "Spine surgeries performed" },
  { value: "Complex", label: "Trauma to revision spine care" },
  { value: "SUHAM", label: "Senior Consultant Spine Surgeon" },
];

export const aboutParagraphs = [
  "Ortho Spine Care is a dedicated orthopaedic and spine surgery practice based in Srirangam, Tiruchirappalli (Trichy). The practice is led by Dr. N. Ajit Kumar, Senior Consultant in Orthopaedic and Spine Surgery, whose patient-centric approach has earned the trust of patients across the region.",
  "Dr. Ajit Kumar is well trained in handling complex trauma, joint replacement, and complex and revision spine surgeries. He currently serves as a Senior Consultant Spine Surgeon at SUHAM Hospitals.",
  "Our focus is simple: accurate diagnosis, clear communication, and a personalised treatment plan that puts the patient first — from first consultation through recovery and rehabilitation.",
];

export const mainServices = [
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20modern%20medical%20still%20life%20of%20a%20detailed%20human%20spine%20model%20beside%20a%20spine%20X-ray%20on%20a%20clean%20white%20desk%20in%20a%20premium%20orthopaedic%20clinic%2C%20soft%20royal%20blue%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20high%20detail%20editorial%20photography&width=1000&height=750&seq=ospc-svc-spine-2026&orientation=landscape",
    alt: "Spine disorders treated at Ortho Spine Care in Trichy",
    title: "Spine Disorders",
    items: [
      "Cervical disc prolapse",
      "Lumbar disc prolapse",
      "Spine trauma",
      "Spondylolisthesis",
      "Spine infections",
      "Spine deformity",
      "Spinal stenosis",
      "Spine tumour",
      "Revision spine surgeries",
    ],
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20knee%20and%20shoulder%20joint%20models%20arranged%20on%20a%20clean%20white%20surface%20in%20a%20modern%20orthopaedic%20clinic%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-joint-2026&orientation=landscape",
    alt: "Joint disorders and joint replacement services in Trichy",
    title: "Joint Disorders",
    items: [
      "Joint replacement surgeries for arthritis",
      "Arthroscopy for joint ligament injuries",
    ],
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20close%20up%20of%20anatomical%20models%20of%20the%20shoulder%20rotator%20cuff%20and%20ankle%20tendon%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%2C%20soft%20teal%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-tendon-2026&orientation=landscape",
    alt: "Tendon disorder treatment including rotator cuff and heel tendon injuries",
    title: "Tendon Disorders",
    items: [
      "Rotator cuff injuries of the shoulder",
      "Wrist and hand tendon injuries",
      "Heel tendon injuries",
    ],
  },
];

export const moreServices = [
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20skeletal%20leg%20and%20bone%20alignment%20model%20on%20a%20clean%20white%20surface%20in%20a%20modern%20orthopaedic%20consulting%20room%2C%20soft%20navy%20and%20cyan%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-bonedeformity-2026&orientation=landscape",
    alt: "Bone deformity diagnosis and treatment",
    title: "Bone Deformities",
    text: "Quality diagnosis, treatment and management of bone deformities, delivered with experienced and compassionate care.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20bone%20scan%20and%20MRI%20images%20displayed%20on%20a%20modern%20lightbox%20in%20a%20clean%20dark%20radiology%20room%2C%20soft%20violet%20and%20blue%20glow%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-bonetumour-2026&orientation=landscape",
    alt: "Bone tumour evaluation and treatment",
    title: "Bone Tumours",
    text: "Personalised, trusted care for bone tumours, with an experienced team committed to finding the best possible solution.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20close%20up%20of%20a%20lower%20leg%20bone%20model%20with%20a%20clean%20medical%20dressing%20and%20medication%20vial%20on%20a%20white%20surface%20in%20a%20bright%20clinic%2C%20soft%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-boneinfection-2026&orientation=landscape",
    alt: "Bone infection treatment and bone health restoration",
    title: "Bone Infections",
    text: "Relief from bone infections, focused on controlling the infection and restoring bone health.",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Photorealistic%20X-ray%20of%20a%20fractured%20long%20bone%20displayed%20on%20a%20clean%20lightbox%20in%20a%20modern%20orthopaedic%20clinic%2C%20soft%20blue%20and%20coral%20accent%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography&width=1000&height=750&seq=ospc-svc-fracture-2026&orientation=landscape",
    alt: "Complex bone fracture management and fixation",
    title: "Complex Bone Fracture Management",
    text: "Expert treatment for complex bone fractures, with a focus on safe and successful recovery.",
  },
];

export const spineProcedures = [
  "Cervical disc prolapse surgery",
  "Lumbar disc prolapse surgery",
  "Spine trauma stabilisation",
  "Spondylolisthesis correction",
  "Spine infection management",
  "Spine deformity correction",
  "Spinal stenosis decompression",
  "Spine tumour surgery",
  "Revision spine surgery",
];

export const spineHighlights = [
  {
    icon: "ri-body-scan-line",
    title: "Minimally Invasive Spine Surgery",
    text: "Procedures designed to reduce tissue damage and support a faster return to daily activity, aimed at lasting relief from long-standing neck and back pain.",
  },
  {
    icon: "ri-refresh-line",
    title: "Complex & Revision Spine Surgery",
    text: "Specialised care for complex and revision spine cases, drawing on extensive surgical experience at a senior consultant level.",
  },
  {
    icon: "ri-heart-2-line",
    title: "Recovery-Focused Care",
    text: "Structured follow-up and rehabilitation, supported by a well-equipped ICU and a trained post-operative care team for high-risk patients.",
  },
];

export const orthopaedicServices = [
  { icon: "ri-walk-line", title: "Joint Replacement", text: "Joint replacement surgeries for arthritis to restore mobility and reduce pain." },
  { icon: "ri-focus-3-line", title: "Arthroscopy", text: "Keyhole arthroscopy for joint ligament injuries of the knee and shoulder." },
  { icon: "ri-first-aid-kit-line", title: "Complex Trauma & Fractures", text: "Management of complex trauma and complex bone fracture fixation." },
  { icon: "ri-shield-cross-line", title: "Bone Infections", text: "Diagnosis and treatment of bone infections with a focus on recovery." },
  { icon: "ri-heart-add-line", title: "Bone Deformities", text: "Assessment and management of bone deformities and alignment problems." },
  { icon: "ri-hand-heart-line", title: "Tendon & Soft Tissue", text: "Care for rotator cuff, wrist, hand and heel tendon injuries." },
];

export const doctors = [
  {
    image: "https://storage.helloreaddy.io/project_files/afe7bf6d-1a42-42e3-a1b8-8dc30048a3f0/f334d67d-5810-4325-9e00-5d6e70a0e5a2_compressed_WhatsApp-Image-2022-11-11-at-21.00.02.webp",
    alt: "Dr. N. Ajit Kumar, Senior Consultant in Orthopaedic and Spine Surgery, Trichy",
    name: "Dr. N. Ajit Kumar",
    designation: "Senior Consultant – Orthopaedic & Spine Surgery",
    qualification: "",
    specialty: "Orthopaedic & Spine Surgery",
    affiliation: "Senior Consultant Spine Surgeon, SUHAM Hospitals",
    experience: "10+ years of surgical experience · 2000+ spine surgeries",
    bio: "Dr. Ajit Kumar is known for his patient-centric approach to the treatment of trauma, joint disorders and various spine-related diseases. He is well trained in handling complex trauma, joint replacement, and complex and revision spine surgeries.",
    expertise: [
      "Complex trauma surgery",
      "Joint replacement",
      "Complex spine surgery",
      "Revision spine surgery",
      "Minimally invasive spine procedures",
    ],
  },
];

export const whyChooseUs = [
  { icon: "ri-award-line", title: "Experienced Specialist", text: "10+ years of experience and 2000+ spine surgeries, from complex to revision cases." },
  { icon: "ri-hospital-line", title: "Comprehensive Orthopaedic Care", text: "Joint, trauma, bone and tendon conditions managed under one specialist practice." },
  { icon: "ri-body-scan-line", title: "Spine Care Expertise", text: "Focused, senior-level experience in spine disorders and minimally invasive techniques." },
  { icon: "ri-microscope-line", title: "Modern Techniques", text: "Arthroscopy, arthroplasty and minimally invasive approaches where suitable." },
  { icon: "ri-user-heart-line", title: "Patient-Centred Approach", text: "Clear communication and personalised treatment decisions at every step." },
  { icon: "ri-heart-pulse-line", title: "Supported Recovery", text: "Well-equipped ICU and trained staff supporting post-operative care and rehabilitation." },
];

export const patientJourney = [
  { step: "01", title: "Book Appointment", text: "Request a visit online or call the clinic to choose a convenient time." },
  { step: "02", title: "Specialist Consultation", text: "Meet the specialist to discuss your symptoms, history and concerns." },
  { step: "03", title: "Diagnosis", text: "Clinical examination and appropriate imaging to reach a clear diagnosis." },
  { step: "04", title: "Personalised Treatment", text: "A treatment plan tailored to your condition, with non-surgical options considered first." },
  { step: "05", title: "Follow-Up & Recovery", text: "Structured follow-up and rehabilitation support through your recovery." },
];

export const blogPosts = [
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20calm%20modern%20medical%20consultation%20room%20with%20a%20spine%20model%20and%20X-ray%20on%20a%20clean%20desk%2C%20soft%20blue%20and%20cyan%20lighting%2C%20premium%20healthcare%20editorial%20photography&width=800&height=600&seq=ospc-mock-blog-spine-2026&orientation=landscape",
    alt: "Understanding lumbar disc prolapse and its treatment",
    category: "Spine Health",
    title: "Understanding Lumbar Disc Prolapse",
    excerpt: "What a slipped disc in the lower back actually means, the symptoms to watch for, and when to seek specialist care.",
    date: "12 Aug 2026",
    readTime: "5 min read",
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20knee%20joint%20model%20and%20an%20X-ray%20on%20a%20clean%20consultation%20desk%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%20and%20cyan%20lighting%2C%20premium%20medical%20editorial%20photography&width=800&height=600&seq=ospc-mock-blog-education-2026&orientation=landscape",
    alt: "When to see a spine specialist for neck and back pain",
    category: "Patient Education",
    title: "When Should You See a Spine Specialist?",
    excerpt: "Most back and neck pain settles on its own — here are the signs that mean it is time for a specialist opinion.",
    date: "28 Jul 2026",
    readTime: "4 min read",
  },
  {
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20anatomical%20knee%20joint%20model%20on%20a%20clean%20white%20surface%20in%20a%20bright%20clinic%2C%20soft%20teal%20and%20cyan%20lighting%2C%20premium%20medical%20editorial%20photography&width=800&height=600&seq=ospc-mock-blog-joint-2026&orientation=landscape",
    alt: "Managing knee arthritis and joint pain",
    category: "Joint Pain",
    title: "Managing Knee Arthritis: Options That Help",
    excerpt: "From activity changes and physiotherapy to joint replacement, a look at the treatment pathway for knee arthritis.",
    date: "09 Jul 2026",
    readTime: "6 min read",
  },
];

export const faqs = [
  {
    q: "When should I consult an orthopaedic specialist?",
    a: "Consider a specialist opinion if pain, swelling or stiffness in a bone or joint lasts more than a few weeks, limits daily activities, follows an injury, or is accompanied by numbness or weakness. Early assessment often allows simpler treatment options.",
  },
  {
    q: "When should I see a spine specialist?",
    a: "See a spine specialist if you have neck or back pain that persists despite rest and medication, pain that travels into an arm or leg, numbness or weakness, or difficulty with balance or walking. Red-flag symptoms such as loss of bladder or bowel control need urgent attention.",
  },
  {
    q: "What causes back pain?",
    a: "Common causes include muscle strain, poor posture, disc problems, spinal stenosis, arthritis of the spine, and injuries. Most cases improve with conservative care, but persistent or severe pain should be evaluated to identify the underlying cause.",
  },
  {
    q: "What treatments are available for joint pain?",
    a: "Treatment depends on the cause and severity. Options range from activity modification, physiotherapy and medication to injections and, in appropriate cases, joint replacement or arthroscopic surgery. Your specialist will discuss the options suited to you.",
  },
  {
    q: "Is spine surgery always necessary for disc problems?",
    a: "No. Many disc problems improve with non-surgical treatment such as physiotherapy and medication. Surgery is usually considered when symptoms are severe, do not improve with conservative care, or involve progressive nerve weakness.",
  },
  {
    q: "How can I book an appointment?",
    a: "You can request an appointment through the booking form on this website, or call the clinic directly on +91 99433 16050. Our team will confirm a suitable time with you.",
  },
  {
    q: "What should I bring for my consultation?",
    a: "Please bring any previous medical records, reports and imaging (X-ray, MRI or CT scans) if available, a list of current medications, and details of your symptoms including when they started.",
  },
];

export const formDepartments = [
  "Orthopaedic Care",
  "Spine Care",
  "Joint Replacement",
  "Arthroscopy",
  "Trauma & Fracture Care",
  "Bone & Joint Infections",
  "Not sure — please advise",
];